/**
 * RiskAssessmentWorkspace — three-pane risk surface that replaces the legacy
 * single-column EvaluationRisks tab.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │           RiskProfileSummary                │  G2 — 4 stat tiles
 *   ├─────────────────────────────┬───────────────┤
 *   │   RiskRegisterPanel         │   HeatMap     │  G2     G1
 *   │   (table / cards)           │   5×5 grid    │
 *   └─────────────────────────────┴───────────────┘
 *
 * Mobile (<lg): panes stack vertically (summary → heatmap → register) so the
 * heatmap stays visible without horizontal scroll, per acceptance criteria.
 *
 * One shared RiskFormSlideOver is owned here. Both the register row click
 * and the HeatMap edit-pencil click route to the same slide-over via
 * onAddRisk / onEditRisk callbacks. On close the workspace dispatches
 * fetchRisksComparison so newly created / edited risks land in both panes.
 *
 * Linear: MYD-214 (G3). Replaces the legacy EvaluationRisks single-column
 * UI.
 */

import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ShieldAlert, LayoutTemplate, Save, Sparkles } from 'lucide-react'
import { Button } from 'components/ui/button'
import { isAskMdtAvailable } from 'features/AskMdt/availability'
import { RiskSuggestionsSheet, type RiskToApply } from 'features/AskMdt/components/RiskSuggestionsSheet'
import getCapabilities from 'utils/capabilities'

import { createRisk, fetchRisksComparison } from 'slices/riskSlice'

import HeatMap from './HeatMap'
import RiskProfileSummary from './RiskProfileSummary'
import RiskRegisterPanel from './RiskRegisterPanel'
import RiskFormSlideOver from './RiskFormSlideOver'
import ApplyTemplateModalCN from '../templates/ApplyTemplateModalCN'
import SaveAsTemplateModalCN from '../templates/SaveAsTemplateModalCN'

interface RiskAssessmentWorkspaceProps {
	evalId: string
}

const RiskAssessmentWorkspace = ({ evalId }: RiskAssessmentWorkspaceProps) => {
	const dispatch = useDispatch()

	const [formOpen, setFormOpen] = useState(false)
	const [formRisk, setFormRisk] = useState<any>(null)
	const [formSiteId, setFormSiteId] = useState<string>('')

	const [applyOpen, setApplyOpen] = useState(false)
	const [saveAsOpen, setSaveAsOpen] = useState(false)

	const comparison = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)

	// Snapshot for Save-As: collect risks from across all sites in the eval,
	// deduplicated by name. The user has typically applied a template to all
	// sites and then edited some — saving the union (deduped) captures the
	// curated rubric. Comparison data carries name/category/likelihood/impact
	// but not mitigation/contingency; admin can fill those in post-snapshot.
	const sourceItems = useMemo(() => {
		const byName: Record<string, any> = {}
		for (const site of comparison) {
			for (const risk of site.risks || []) {
				if (!risk.name) continue
				if (!byName[risk.name]) {
					byName[risk.name] = {
						name: risk.name,
						category: risk.category,
						likelihood: risk.likelihood,
						impact: risk.impact,
					}
				}
			}
		}
		return Object.values(byName)
	}, [comparison])

	// "Suggest risks" (MYD-647), where Ask MDT is offered. Apply is off where
	// the create-risk endpoint would refuse it: read-only or impersonating.
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const askAvailable = isAskMdtAvailable(userInfo)
	const canWrite = getCapabilities(userInfo).canWrite
	const [suggestOpen, setSuggestOpen] = useState(false)
	const suggestSites = useMemo(
		() => comparison.map((site: any) => ({ id: String(site.siteId), name: site.siteName })),
		[comparison],
	)

	// The rows the user kept, created one at a time through the ordinary risk
	// endpoint. Stops at the first failure and says how far it got, rather than
	// leaving a half-applied set unexplained.
	const applySuggestions = useCallback(
		async (siteId: string, risks: RiskToApply[]) => {
			let saved = 0
			try {
				for (const payload of risks) {
					// @ts-ignore — thunk
					await dispatch(createRisk({ evalId, siteId, payload })).unwrap()
					saved += 1
				}
			} catch (err: any) {
				throw new Error(`${saved} of ${risks.length} risks were added, then: ${typeof err === 'string' ? err : 'a risk failed to save'}.`)
			} finally {
				// @ts-ignore — thunk
				dispatch(fetchRisksComparison(evalId))
			}
		},
		[dispatch, evalId],
	)

	const handleTemplateApplied = useCallback(() => {
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const handleAdd = useCallback((siteId: string) => {
		setFormRisk(null)
		setFormSiteId(siteId)
		setFormOpen(true)
	}, [])

	const handleEdit = useCallback((risk: any) => {
		setFormRisk(risk)
		setFormSiteId(typeof risk.site === 'string' ? risk.site : String(risk.site))
		setFormOpen(true)
	}, [])

	const handleFormClose = useCallback(() => {
		setFormOpen(false)
		setFormRisk(null)
		setFormSiteId('')
		// Refresh the comparison so both panes pick up the change.
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex flex-row items-start justify-between gap-3">
				<div>
					<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
						<ShieldAlert className="w-5 h-5 text-[#0080fe]" />
						Risks
					</h3>
					<p className="text-sm text-gray-500">
						Track risks per site, see them on the likelihood × impact heat map, and review the
						profile at a glance.
					</p>
				</div>
				<div className="flex items-center gap-1.5 shrink-0">
					{askAvailable && suggestSites.length > 0 && (
						<Button
							variant="primary"
							isOutline
							onClick={() => setSuggestOpen(true)}
							className="gap-1.5"
							title="Suggest risks for a site with AI"
						>
							<Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
							Suggest risks
						</Button>
					)}
					<Button
						variant="primary"
						isOutline
						onClick={() => setApplyOpen(true)}
						className="gap-1.5"
						title="Apply a risk template across this evaluation's sites"
						aria-label="Apply risk template"
					>
						<LayoutTemplate className="w-3.5 h-3.5" />
					</Button>
					<Button
						variant="primary"
						isOutline
						onClick={() => setSaveAsOpen(true)}
						disabled={sourceItems.length === 0}
						className="gap-1.5"
						title={
							sourceItems.length === 0
								? 'Add at least one risk to a site before snapshotting'
								: 'Save the current curated risks as a reusable template'
						}
						aria-label="Save as risk template"
					>
						<Save className="w-3.5 h-3.5" />
					</Button>
				</div>
			</div>

			{/* Top row: stats */}
			<RiskProfileSummary evalId={evalId} />

			{/* Bottom row: register + heatmap.
			    - mobile / md: stack vertically (heatmap above register so it
			      stays visible per acceptance criterion)
			    - lg+: 12-col grid with 7/5 split (register larger, heatmap on right)
			*/}
			<div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
				<div className="order-2 lg:order-1 lg:col-span-7">
					<RiskRegisterPanel
						evalId={evalId}
						onAddRisk={handleAdd}
						onEditRisk={handleEdit}
					/>
				</div>
				<div className="order-1 lg:order-2 lg:col-span-5">
					<HeatMap evalId={evalId} onRiskEdit={handleEdit} />
				</div>
			</div>

			{/* Shared slide-over — single instance for both pane edits + HeatMap edits. */}
			<RiskFormSlideOver
				open={formOpen}
				onClose={handleFormClose}
				evalId={evalId}
				siteId={formSiteId}
				risk={formRisk}
			/>

			<ApplyTemplateModalCN
				open={applyOpen}
				onOpenChange={setApplyOpen}
				templateType="risk"
				evalId={evalId}
				onApplied={handleTemplateApplied}
			/>

			<SaveAsTemplateModalCN
				open={saveAsOpen}
				onOpenChange={setSaveAsOpen}
				templateType="risk"
				sourceItems={sourceItems}
			/>

			{/* Stays mounted once offered: the Sheet must see `open` go false to
			    play its exit and give the page its scroll back. */}
			{askAvailable && suggestSites.length > 0 && (
				<RiskSuggestionsSheet
					open={suggestOpen}
					onClose={() => setSuggestOpen(false)}
					evaluationId={evalId}
					sites={suggestSites}
					canApply={canWrite}
					onApply={applySuggestions}
				/>
			)}
		</div>
	)
}

export default RiskAssessmentWorkspace
